import { getIndex, type IndexIndicatorItem } from "@/repo";
import ControlPanelListSearch from "./ControlPanelListSearch";
import { useEffect, useMemo, useState, type RefObject } from "react";
import "./ControlPanelList.css";
import "./ControlPanelSelectedEntity.css";
import type { Map } from "mapbox-gl";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { getIndicatorStat } from "../../repo/indicator";
import { ResponsiveBar } from "@nivo/bar";
import { createMapboxExtrusion, createMapboxFillAndOpacity } from "@/constants";
import { useNavigate, useParams } from "react-router-dom";
import { getEnvParam, type TileEnv } from "@/env";

interface SelectedIndicatorProps {
    onClose(): void;
    indicator: IndexIndicatorItem;
}

function SelectedIndicator(props: SelectedIndicatorProps) {
    const indicatorQuery = useQuery({
        queryKey: ["indicator", props.indicator.id],
        queryFn: () => getIndicatorStat(props.indicator.id),
    });

    const indexQuery = useQuery({
        queryKey: ["index"],
        queryFn: getIndex,
    });

    const knownCollections = useMemo(() => {
        if (!indexQuery.isSuccess) return [];

        return indexQuery.data.collections.filter((collection) =>
            collection.indicators.some((i) => props.indicator.id === i.id),
        );
    }, [indexQuery.data]);

    const knownAttributes = useMemo(() => {
        if (!indexQuery.isSuccess) return [];

        return indexQuery.data.attributes.filter((attribute) =>
            attribute.collections.some((c) =>
                knownCollections.some((kc) => kc.id === c.id),
            ),
        );
    }, [indexQuery.data]);

    return (
        <div className="selected-entity">
            <div className="selected-entity-title">
                <button
                    className="selected-entity-title__exit"
                    onClick={() => props.onClose()}
                >
                    ←
                </button>
                <div className="selected-entity-title__main">
                    <h3 className="selected-entity-title__name">
                        {props.indicator.name}
                    </h3>
                    <span className="selected-entity-title__id">
                        ID: {props.indicator.id}
                    </span>
                </div>
            </div>

            {indicatorQuery.isSuccess && indexQuery.isSuccess ? (
                <div className="selected-entity-content">
                    <h4>Info</h4>
                    <dl>
                        <dt>Description</dt>
                        <dd>{props.indicator.description}</dd>
                        <dt>Indicator Unit</dt>
                        <dd>{props.indicator.unit}</dd>
                    </dl>
                    <hr />
                    <h4>Stat</h4>
                    <dl>
                        <dt>Min Value</dt>
                        <dd>{indicatorQuery.data.min}</dd>
                        <dt>Max Value</dt>
                        <dd>{indicatorQuery.data.max}</dd>
                        <dt>Record Count</dt>
                        <dd>{indicatorQuery.data.count}</dd>
                    </dl>
                    <hr />
                    <h4>Distribution - WIP!</h4>
                    <div style={{ height: "300px" }}>
                        <ResponsiveBar
                            data={indicatorQuery.data.distribution.map(
                                (d, index) => ({
                                    ...d,
                                    binLabel: `${d.min.toFixed(2)} - ${d.max.toFixed(2)}`,
                                    index,
                                }),
                            )}
                            keys={["count"]}
                            indexBy="binLabel"
                            margin={{
                                top: 30,
                                right: 20,
                                bottom: 100,
                                left: 50,
                            }}
                            padding={0.1}
                            valueScale={{
                                type: "linear",
                                round: true,
                            }}
                            indexScale={{ type: "band", round: true }}
                            colors={"#ea7d5455"}
                            borderRadius={0}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: -45,
                            }}
                            axisLeft={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                            }}
                            enableLabel={false}
                            theme={{
                                grid: {
                                    line: {
                                        stroke: "#ffffff11",
                                        strokeWidth: 1,
                                    },
                                },

                                axis: {
                                    ticks: {
                                        text: { fill: "#ffffffaa" },
                                    },
                                    legend: {
                                        text: { fill: "#8888ff" },
                                    },
                                },
                            }}
                            borderColor="#ea7d54"
                            borderWidth={1}
                            tooltip={({ data, value }) => (
                                <div
                                    style={{
                                        padding: 12,
                                        color: "#fff",
                                        background: "#222",
                                        borderRadius: 4,
                                    }}
                                >
                                    <strong>Range:</strong>{" "}
                                    {data.min.toFixed(4)} to{" "}
                                    {data.max.toFixed(4)}
                                    <br />
                                    <strong>Count:</strong> {value}
                                </div>
                            )}
                            role="application"
                            ariaLabel="Indicator Distribution Histogram"
                        />
                    </div>

                    <hr />

                    <h4>In Collections</h4>
                    <ul>
                        {knownCollections.length ? (
                            knownCollections.map((collection) => (
                                <li>{collection.name}</li>
                            ))
                        ) : (
                            <li>None</li>
                        )}
                    </ul>

                    <hr />

                    <h4>In Attributes</h4>
                    <ul>
                        {knownAttributes.length ? (
                            knownAttributes.map((attribute) => (
                                <li>{attribute.name}</li>
                            ))
                        ) : (
                            <li>None</li>
                        )}
                    </ul>
                </div>
            ) : (
                <span>Loading</span>
            )}
        </div>
    );
}

interface LocationIndicatorListProps {
    indicators: IndexIndicatorItem[];
    map: RefObject<Map | undefined>;
    env: TileEnv;
}

function LocationIndicatorList(props: LocationIndicatorListProps) {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const selectedId = id ? Number(id) : undefined;

    useEffect(() => {
        if (selectedId === undefined) return;
        const map = props.map.current;
        if (!map) return;

        if (map.getSource("mvt-tiles")) {
            if (map.getLayer("tiles")) map.removeLayer("tiles");
            if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
            map.removeSource("mvt-tiles");
        }

        map.addSource("mvt-tiles", {
            type: "vector",
            tiles: [
                `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/indicators/${selectedId}/{z}/{x}/{y}?${getEnvParam()}`,
            ],
        });

        map.addLayer({
            id: "tiles",
            type: "fill",
            source: "mvt-tiles",
            "source-layer": "values",
            paint: createMapboxFillAndOpacity(
                ["get", "value_normalized"],
                0.001,
            ),
        });

        map.addLayer({
            id: "tiles-values",
            type: "symbol",
            source: "mvt-tiles",
            "source-layer": "values",
            minzoom: 12.5,
            paint: {
                "text-color": "#fff",
                "text-opacity": [
                    "interpolate",
                    ["linear"],
                    ["get", "value_normalized"],
                    0,
                    0.4,
                    1,
                    0.6,
                ],
            },
            layout: {
                "text-field": ["get", "value"],
                "text-font": ["Source Code Pro", "Arial Unicode MS Bold"],
                "text-offset": [0, 0],
                "text-size": 8,
                "text-anchor": "center",
            },
        });

        return () => {
            const m = props.map.current;
            if (!m) return;
            if (m.getLayer("tiles-values")) m.removeLayer("tiles-values");
            if (m.getLayer("tiles")) m.removeLayer("tiles");
            if (m.getSource("mvt-tiles")) m.removeSource("mvt-tiles");
        };
    }, [selectedId, props.map, props.env]);

    function handleIndicatorSelect(indicatorId: number) {
        setSearch("");
        navigate(`/location/indicators/${indicatorId}`);
    }

    const indicators = useMemo(() => {
        return props.indicators.filter((indicator) =>
            indicator.name
                .toLowerCase()
                .trim()
                .includes(search.toLowerCase().trim()),
        );
    }, [props.indicators, search]);

    const selectedIndicator = props.indicators.find(
        (indicator) => indicator.id === selectedId,
    );

    return (
        <>
            {!selectedIndicator ? (
                <>
                    <ControlPanelListSearch
                        type="indicator"
                        search={search}
                        setSearch={setSearch}
                    />
                    <div className="control-panel-button-list">
                        {indicators.map((indicator) => (
                            <button
                                key={indicator.id}
                                onClick={() =>
                                    handleIndicatorSelect(indicator.id)
                                }
                                className={classNames(
                                    "control-panel-button-list__item",
                                    indicator.id === selectedId &&
                                        "control-panel-button-list__item--active",
                                )}
                            >
                                <div></div>
                                {indicator.name}
                            </button>
                        ))}
                    </div>
                </>
            ) : (
                <SelectedIndicator
                    indicator={selectedIndicator}
                    onClose={() => navigate("/location/indicators")}
                />
            )}
        </>
    );
}

export default LocationIndicatorList;
